import { ThemeConfig } from 'antd';

export const lightTheme: ThemeConfig = {
  token: {
    // Primary colors - Professional blue palette for admin interfaces
    colorPrimary: '#1e3a8a', // Deep blue
    colorPrimaryBg: '#eff6ff',
    colorPrimaryBgHover: '#dbeafe',
    colorPrimaryBorder: '#93c5fd',
    colorPrimaryBorderHover: '#60a5fa',
    colorPrimaryHover: '#2563eb',
    colorPrimaryActive: '#1d4ed8',
    colorPrimaryTextHover: '#1e40af',
    colorPrimaryText: '#1e3a8a',
    colorPrimaryTextActive: '#1e3a8a',

    // Success colors - Professional green
    colorSuccess: '#059669',
    colorSuccessBg: '#f0fdf4',
    colorSuccessBgHover: '#dcfce7',
    colorSuccessBorder: '#86efac',
    colorSuccessBorderHover: '#4ade80',
    colorSuccessHover: '#10b981',
    colorSuccessActive: '#047857',
    colorSuccessTextHover: '#065f46',
    colorSuccessText: '#059669',
    colorSuccessTextActive: '#059669',

    // Warning colors - Professional amber
    colorWarning: '#d97706',
    colorWarningBg: '#fffbeb',
    colorWarningBgHover: '#fef3c7',
    colorWarningBorder: '#fcd34d',
    colorWarningBorderHover: '#fbbf24',
    colorWarningHover: '#f59e0b',
    colorWarningActive: '#b45309',
    colorWarningTextHover: '#92400e',
    colorWarningText: '#d97706',
    colorWarningTextActive: '#d97706',

    // Error colors - Professional red
    colorError: '#dc2626',
    colorErrorBg: '#fef2f2',
    colorErrorBgHover: '#fee2e2',
    colorErrorBorder: '#fca5a5',
    colorErrorBorderHover: '#f87171',
    colorErrorHover: '#ef4444',
    colorErrorActive: '#b91c1c',
    colorErrorTextHover: '#991b1b',
    colorErrorText: '#dc2626',
    colorErrorTextActive: '#dc2626',

    // Neutral colors
    colorTextBase: '#1f2937',
    colorBgBase: '#ffffff',
    colorText: '#374151',
    colorTextSecondary: '#6b7280',
    colorTextTertiary: '#9ca3af',
    colorTextQuaternary: '#d1d5db',
    colorBorder: '#e5e7eb',
    colorBorderSecondary: '#f3f4f6',
    colorSplit: '#f3f4f6',
    colorBgContainer: '#ffffff',
    colorBgElevated: '#ffffff',
    colorBgLayout: '#f9fafb',
    colorBgSpotlight: '#ffffff',
    colorBgMask: 'rgba(0, 0, 0, 0.45)',

    // Component specific
    colorFillAlter: '#f9fafb',
    colorFillContent: '#f3f4f6',
    colorFillContentHover: '#e5e7eb',
    colorFillSecondary: '#f3f4f6',
    colorFillTertiary: '#f9fafb',
    colorFillQuaternary: '#ffffff',

    // Border radius for modern look
    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 6,
    borderRadiusXS: 4,

    // Shadows for elevation
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    boxShadowSecondary: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    boxShadowTertiary: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',

    // Typography
    fontSize: 14,
    fontSizeLG: 16,
    fontSizeXL: 20,
    fontSizeHeading1: 38,
    fontSizeHeading2: 30,
    fontSizeHeading3: 24,
    fontSizeHeading4: 20,
    fontSizeHeading5: 16,
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontFamilyCode: '"SF Mono", "Monaco", "Inconsolata", "Roboto Mono", "Source Code Pro", monospace',

    // Line height for better readability
    lineHeight: 1.5715,
    lineHeightLG: 1.5,
    lineHeightSM: 1.66,

    // Control heights for better touch targets
    controlHeight: 36,
    controlHeightLG: 44,
    controlHeightSM: 28,
    controlHeightXS: 24,

    // Motion
    motionDurationFast: '0.1s',
    motionDurationMid: '0.2s',
    motionDurationSlow: '0.3s',
    motionEaseInOut: 'cubic-bezier(0.645, 0.045, 0.355, 1)',
    motionEaseOut: 'cubic-bezier(0.215, 0.61, 0.355, 1)',
    motionEaseInBack: 'cubic-bezier(0.71, -0.46, 0.88, 0.6)',
    motionEaseOutBack: 'cubic-bezier(0.18, 0.89, 0.32, 1.28)',
  },
  components: {
    Layout: {
      siderBg: '#1f2937',
      triggerBg: '#374151',
      triggerColor: '#ffffff',
      headerBg: '#ffffff',
      headerHeight: 64,
      headerPadding: '0 24px',
      footerBg: '#f9fafb',
      bodyBg: '#f9fafb',
    },
    Menu: {
      itemBg: 'transparent',
      itemColor: '#d1d5db',
      itemHoverBg: 'rgba(59, 130, 246, 0.1)',
      itemHoverColor: '#ffffff',
      itemSelectedBg: '#1e40af',
      itemSelectedColor: '#ffffff',
      itemActiveBg: '#1e40af',
      subMenuItemBg: '#374151',
      groupTitleColor: '#9ca3af',
      iconSize: 16,
      itemHeight: 42,
      itemMarginBlock: 4,
      itemMarginInline: 8,
      itemPaddingInline: 16,
    },
    Card: {
      headerBg: 'transparent',
      actionsBg: '#f9fafb',
      paddingLG: 24,
      paddingMD: 20,
      paddingSM: 16,
    },
    Table: {
      headerBg: '#f9fafb',
      headerColor: '#374151',
      headerSortActiveBg: '#f3f4f6',
      headerSortHoverBg: '#f3f4f6',
      rowHoverBg: '#f9fafb',
      borderColor: '#e5e7eb',
      footerBg: '#f9fafb',
      cellPaddingBlock: 12,
      cellPaddingInline: 16,
    },
    Button: {
      primaryShadow: '0 2px 0 rgba(30, 58, 138, 0.1)',
      dangerShadow: '0 2px 0 rgba(220, 38, 38, 0.1)',
      defaultShadow: '0 2px 0 rgba(0, 0, 0, 0.02)',
      paddingInlineLG: 24,
      paddingBlockLG: 10,
      paddingInline: 16,
      paddingBlock: 8,
      paddingInlineSM: 12,
      paddingBlockSM: 6,
    },
    Input: {
      paddingInline: 12,
      paddingBlock: 8,
      borderRadius: 6,
      hoverBorderColor: '#60a5fa',
      activeBorderColor: '#2563eb',
      activeShadow: '0 0 0 2px rgba(37, 99, 235, 0.2)',
    },
    Select: {
      borderRadius: 6,
      optionActiveBg: '#eff6ff',
      optionSelectedBg: '#dbeafe',
      optionPadding: '8px 12px',
    },
    DatePicker: {
      borderRadius: 6,
      cellHoverBg: '#eff6ff',
      cellActiveWithRangeBg: '#dbeafe',
      cellRangeBorderColor: '#93c5fd',
    },
    Statistic: {
      titleFontSize: 14,
      contentFontSize: 24,
    },
    Badge: {
      dotSize: 8,
      textFontSize: 12,
      textFontSizeSM: 10,
    },
    Tag: {
      borderRadiusSM: 4,
      paddingInlineSM: 8,
      marginXS: 2,
    },
    Progress: {
      defaultColor: '#1e3a8a',
      remainingColor: '#e5e7eb',
    },
    Alert: {
      borderRadiusLG: 8,
      withDescriptionPaddingVertical: 16,
      withDescriptionPadding: '16px 20px',
    },
    Tooltip: {
      borderRadius: 6,
      paddingXS: 8,
    },
    Drawer: {
      borderRadiusLG: 8,
    },
    Modal: {
      borderRadiusLG: 12,
      headerBg: '#ffffff',
      contentBg: '#ffffff',
      footerBg: '#f9fafb',
    },
  },
};

export const darkTheme: ThemeConfig = {
  ...lightTheme,
  token: {
    ...lightTheme.token,
    // Dark theme overrides
    colorTextBase: '#f9fafb',
    colorBgBase: '#111827',
    colorText: '#e5e7eb',
    colorTextSecondary: '#9ca3af',
    colorTextTertiary: '#6b7280',
    colorTextQuaternary: '#4b5563',
    colorBorder: '#374151',
    colorBorderSecondary: '#1f2937',
    colorSplit: '#374151',
    colorBgContainer: '#1f2937',
    colorBgElevated: '#374151',
    colorBgLayout: '#111827',
    colorBgSpotlight: '#1f2937',
    colorBgMask: 'rgba(0, 0, 0, 0.75)',

    // Component fills for dark mode
    colorFillAlter: '#1f2937',
    colorFillContent: '#374151',
    colorFillContentHover: '#4b5563',
    colorFillSecondary: '#1f2937',
    colorFillTertiary: '#111827',
    colorFillQuaternary: '#000000',
  },
  components: {
    ...lightTheme.components,
    Layout: {
      ...lightTheme.components!.Layout,
      siderBg: '#0f172a',
      triggerBg: '#1e293b',
      headerBg: '#1f2937',
      footerBg: '#111827',
      bodyBg: '#111827',
    },
    Menu: {
      ...lightTheme.components!.Menu,
      itemColor: '#9ca3af',
      itemHoverBg: 'rgba(59, 130, 246, 0.2)',
      itemHoverColor: '#f3f4f6',
      itemSelectedBg: '#2563eb',
      itemSelectedColor: '#ffffff',
      itemActiveBg: '#2563eb',
      subMenuItemBg: '#1e293b',
      groupTitleColor: '#6b7280',
    },
    Card: {
      ...lightTheme.components!.Card,
      actionsBg: '#111827',
    },
    Table: {
      ...lightTheme.components!.Table,
      headerBg: '#111827',
      headerColor: '#e5e7eb',
      headerSortActiveBg: '#1f2937',
      headerSortHoverBg: '#1f2937',
      rowHoverBg: '#1f2937',
      borderColor: '#374151',
      footerBg: '#111827',
    },
    Modal: {
      ...lightTheme.components!.Modal,
      headerBg: '#1f2937',
      contentBg: '#1f2937',
      footerBg: '#111827',
    },
  },
};

// Theme context values
export type ThemeMode = 'light' | 'dark';

export const getTheme = (mode: ThemeMode): ThemeConfig => {
  return mode === 'dark' ? darkTheme : lightTheme;
};

// Admin-specific color palette
export const adminColors = {
  // Status colors
  success: {
    light: '#10b981',
    dark: '#10b981',
    bg: '#f0fdf4',
    bgDark: '#064e3b',
  },
  warning: {
    light: '#f59e0b',
    dark: '#fbbf24',
    bg: '#fffbeb',
    bgDark: '#451a03',
  },
  error: {
    light: '#ef4444',
    dark: '#f87171',
    bg: '#fef2f2',
    bgDark: '#450a0a',
  },
  info: {
    light: '#3b82f6',
    dark: '#60a5fa',
    bg: '#eff6ff',
    bgDark: '#0c1e3f',
  },
  // Partner status colors
  partner: {
    active: '#10b981',
    pending: '#f59e0b',
    suspended: '#ef4444',
    rejected: '#6b7280',
    inactive: '#9ca3af',
  },
  // Booking status colors
  booking: {
    confirmed: '#10b981',
    pending: '#f59e0b',
    cancelled: '#ef4444',
    completed: '#3b82f6',
    no_show: '#8b5cf6',
    disputed: '#f97316',
  },
  // Priority colors
  priority: {
    high: '#ef4444',
    medium: '#f59e0b',
    low: '#3b82f6',
    critical: '#dc2626',
  },
};

// Responsive breakpoints
export const breakpoints = {
  xs: '480px',
  sm: '576px',
  md: '768px',
  lg: '992px',
  xl: '1200px',
  xxl: '1600px',
};

// Animation configurations
export const animations = {
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.2 },
  },
  slideUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
    transition: { duration: 0.3 },
  },
  slideIn: {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 20 },
    transition: { duration: 0.3 },
  },
  scale: {
    initial: { opacity: 0, scale: 0.9 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.9 },
    transition: { duration: 0.2 },
  },
};

// Box shadow configurations
export const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
  none: 'none',
};